// ════════════════════════════════════════════════════════════════════
// Glass-i — Stage SALES-PROFORMA-ACTIVITY-1 — Activity feed helpers (PURE).
//
// Pure, unit-testable ESM used by the drawer's Activity tab. No axios/React.
// Turns a normalised timeline entry ({ kind, eventType, body, remarks,
// actorName, at }) into display text. Time formatting stays in the component
// (dayjs) so these helpers remain locale/timezone-free and deterministic.
// ════════════════════════════════════════════════════════════════════

// System-event → human verb. Present tense reads as a log line ("babu
// confirmed the proforma"). Legacy SAVED/RELEASED still render for old rows.
const EVENT_VERB = {
  CREATED:   'created the proforma',
  UPDATED:   'updated the proforma',
  CONFIRMED: 'confirmed the proforma',
  HELD:      'put the proforma on hold',
  REOPENED:  'moved the proforma to pending',
  CANCELLED: 'cancelled the proforma',
  // Approval workflow (Stage SALES-PROFORMA-APPROVAL-1). These journal events
  // already reach the feed; here they read as the story of the PI's approvals.
  STATUS_CHANGE_REQUESTED:   'requested a status change',
  ACCOUNTS_APPROVAL_CREATED: 'sent the proforma for Accounts review',
  ACCOUNTS_APPROVED:         'approved the proforma (Accounts)',
  ACCOUNTS_REJECTED:         'rejected the proforma (Accounts)',
  ACCOUNTS_HELD:             'put the accounts review on hold',
  ESCALATED_TO_L3:           'escalated the proforma for Management approval',
  L3_APPROVED:               'granted Management approval',
  L3_REJECTED:               'rejected the proforma (Management)',
  WO_READY:                  'marked the Work Order ready',
  // legacy L1 approval (pre drawer-as-L1)
  L1_APPROVED:  'approved the status change',
  L1_REJECTED:  'rejected the status change',
  // legacy journal values (pre-lifecycle rename)
  SAVED:     'saved the proforma',
  RELEASED:  'released the proforma'
};

export function eventVerb(type) {
  const t = type == null ? '' : String(type).toUpperCase();
  return EVENT_VERB[t] || (t ? `changed the proforma (${t.toLowerCase()})` : 'updated the proforma');
}

export function isComment(entry) { return !!entry && entry.kind === 'comment'; }
export function isEvent(entry) { return !!entry && entry.kind === 'event'; }
export function isPayment(entry) { return !!entry && entry.kind === 'payment'; }

// ₹ amount with Indian digit grouping (deterministic; no locale/timezone).
// 40000 → "₹40,000.00". Used only for the money lines in the feed.
export function formatMoney(v) {
  const n = Number(v);
  const amt = Number.isFinite(n) ? n : 0;
  const sign = amt < 0 ? '-' : '';
  const [whole, frac] = Math.abs(amt).toFixed(2).split('.');
  // Indian grouping: last 3 digits, then pairs.
  const last3 = whole.slice(-3);
  const rest = whole.slice(0, -3);
  const grouped = rest ? rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + last3 : last3;
  return `${sign}₹${grouped}.${frac}`;
}

// A payment entry's money story line. Reads as a log line, e.g.
// "posted an advance of ₹40,000.00 (RCT-000123)".
const PAYMENT_PHRASE = {
  RECEIPT_POSTED:    (e) => `posted ${e.purpose === 'RECEIPT' ? 'a receipt' : 'an advance'} of ${formatMoney(e.amount)}`,
  RECEIPT_CANCELLED: (e) => `cancelled a receipt of ${formatMoney(e.amount)}`,
  ADVANCE_ALLOCATED: (e) => `allocated ${formatMoney(e.amount)} of an on-account advance to this proforma`
};
export function paymentText(entry) {
  if (!isPayment(entry)) return '';
  const phrase = PAYMENT_PHRASE[entry && entry.paymentKind];
  const base = phrase ? phrase(entry) : `recorded a payment of ${formatMoney(entry && entry.amount)}`;
  const ref = entry && entry.voucherNo ? entry.voucherNo : (entry && entry.paymentNo) || '';
  return ref ? `${base} (${ref})` : base;
}

// Author label. Comments/events with no resolved user read as "System" (an
// automated or legacy entry), never a blank.
export function activityActorLabel(entry) {
  const n = entry && entry.actorName != null ? String(entry.actorName).trim() : '';
  return n || 'System';
}

// Monogram initials for the entry avatar. Two words → their initials; one word
// → its first two letters; System/empty → "SY"/"·".
export function activityInitials(nameOrEntry) {
  const name = nameOrEntry && typeof nameOrEntry === 'object'
    ? activityActorLabel(nameOrEntry)
    : (nameOrEntry == null ? '' : String(nameOrEntry));
  const s = name.replace(/[^\p{L}\p{N}\s]/gu, ' ').trim();
  if (!s) return '·';
  const words = s.split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return words[0].slice(0, 2).toUpperCase();
}

// The primary line of an entry: a comment's body, or an event's verb. Empty
// comment bodies degrade to '' (the component hides them).
export function entryPrimaryText(entry) {
  if (isComment(entry)) return entry && entry.body != null ? String(entry.body) : '';
  if (isPayment(entry)) return paymentText(entry);
  return eventVerb(entry && entry.eventType);
}

// A short reason/remark shown under a system event (hold/cancel carry one).
// Comments never have a secondary remark.
export function entryRemark(entry) {
  if (!isEvent(entry)) return '';
  return entry && entry.remarks != null ? String(entry.remarks).trim() : '';
}

// Normalise a raw feed payload to a safe, sorted (oldest → newest) entry list.
// Tolerant of null / missing entries; drops non-objects.
export function normalizeEntries(feed) {
  const list = feed && Array.isArray(feed.entries)
    ? feed.entries
    : (Array.isArray(feed) ? feed : []);
  const clean = list.filter((e) => e && typeof e === 'object');
  return clean.slice().sort((a, b) => {
    const at = a.at || ''; const bt = b.at || '';
    if (at !== bt) return at < bt ? -1 : 1;
    return String(a.id) < String(b.id) ? -1 : 1;
  });
}
